import React, { useContext, useState, useEffect, useRef } from 'react';
import { I18nReactContext } from '../../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../../contexts/CourseEditorContext';
import { questionnaireInputFieldType } from '../../../../../Questionnaires/QuestionnaireConstants';
import { validateCourseQuestionAnswerAPI } from '../../../../../../Services/api';
import { get } from 'lodash';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import './QuestionUserView.scss';

/**
 * QuestionUserView - User component for answering course questions in presentation mode
 * Replicates QuestionnaireSessionFrame pattern for consistent UX
 * Takes the whole canvas area like video or background components
 *
 * @param {Object} props - Component props
 * @param {string} props.sectionId - The section ID containing the question
 */
export const QuestionUserView = ({ sectionId }) => {
    const { i18n } = useContext(I18nReactContext);
    const {
        selectedSection,
        signDocId,
        markChapterCompleted,
        completedSections
    } = useContext(CourseEditorContext);

    // Get question data from selected section
    const question = get(selectedSection, 'question', {});
    const questionText = get(question, 'text', '');
    const questionType = get(question, 'type', '');
    const options = get(question, 'options', []);

    // Local state for user answers and validation
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [validationState, setValidationState] = useState(null); // true (correct), false (incorrect), or null (no feedback)
    const [isSubmitting, setIsSubmitting] = useState(false);
    const timeoutRef = useRef(null);

    // Restore saved answers when section changes
    // completedSections is now an object: { sectionId: { selectedOptionIds: [...], answeredAt: '...' } }
    useEffect(() => {
        const sectionData = completedSections[sectionId];

        if (sectionData) {
            // Section is completed - restore the correct answer
            setSelectedAnswers(sectionData.selectedOptionIds || []);
            setValidationState(true);
        } else {
            // Section not completed - start fresh
            setSelectedAnswers([]);
            setValidationState(null);
        }

        // Clear any pending timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [sectionId, completedSections]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleOptionSelect = (optionId) => {
        // Don't allow selection changes while submitting or if correct answer was given
        if (isSubmitting || validationState === true) {
            return;
        }

        setSelectedAnswers(prevAnswers => {
            // For radio (MULTIPLE_CHOICE), only one can be selected
            if (questionType === 'MULTIPLE_CHOICE') {
                return [optionId];
            }
            // For checkbox, toggle selection
            else {
                if (prevAnswers.includes(optionId)) {
                    return prevAnswers.filter(id => id !== optionId);
                } else {
                    return [...prevAnswers, optionId];
                }
            }
        });
    };

    const handleSubmit = async () => {
        if (selectedAnswers.length === 0 || isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await validateCourseQuestionAnswerAPI({
                signDocId,
                sectionId,
                selectedOptionIds: selectedAnswers
            });

            const isCorrect = get(response, 'isCorrect', false);

            if (isCorrect) {
                setValidationState(true);
                // Mark section complete immediately on correct answer
                // CRITICAL: Pass selectedAnswers to persist in backend (UserSignDoc)
                // Backend stores in completedSections object and returns it
                await markChapterCompleted(sectionId, selectedAnswers);
            } else {
                setValidationState(false);
                // Don't save incorrect answers - user starts fresh next time
            }

            // Clear feedback after 3 seconds (only for incorrect answers)
            // Correct answers stay visible permanently
            if (!isCorrect) {
                timeoutRef.current = setTimeout(() => setValidationState(null), 2000);
            }
        } catch (error) {
            console.error('Error validating answer:', error);
            // Reset state on error
            setValidationState(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Use default text if questionText is empty
    const displayQuestionText = questionText || i18n.translate('weezbeeCourse.question.defaultText');

    // Determine feedback text (only show when validationState is true or false)
    let feedbackText = '';
    let FeedbackIcon = null;
    if (validationState === true) {
        feedbackText = i18n.translate('weezbeeCourse.question.correct');
        FeedbackIcon = CheckCircleIcon;
    } else if (validationState === false) {
        feedbackText = i18n.translate('weezbeeCourse.question.tryAgain');
        FeedbackIcon = CancelIcon;
    }

    return (
        <div className="CourseQuestionFrame" data-testid="course-question-user-view">
            <div className="CourseQuestionFrame__content">
                <h2 className="CourseQuestionFrame__question" data-testid="course-question-text">
                    {displayQuestionText}
                </h2>
                <div className="CourseQuestionFrame__answers" data-testid="course-question-options">
                    {options.map((option) => {
                        const optionId = get(option, 'id', '');
                        const optionText = get(option, 'text', '');
                        const isSelected = selectedAnswers.includes(optionId);

                        return (
                            <div
                                key={optionId}
                                className={`CourseQuestionFrame__answer ${isSelected ? 'selected' : ''} ${validationState === true ? 'disabled' : ''}`}
                                onClick={() => handleOptionSelect(optionId)}
                                data-testid={`course-answer-option-${optionId}`}
                            >
                                <div className="CourseQuestionFrame__answer-selector">
                                    <input
                                        type={questionnaireInputFieldType[questionType]}
                                        name="course-question-answer"
                                        checked={isSelected}
                                        readOnly
                                        data-testid={`course-answer-input-${optionId}`}
                                    />
                                </div>
                                <div className="CourseQuestionFrame__answer-text" data-testid={`course-answer-text-${optionId}`}>
                                    {optionText}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Display feedback OR submit button, never both */}
                {validationState !== null ? (
                    <div
                        className={`CourseQuestionFrame__feedback ${validationState === true ? 'correct' : 'incorrect'}`}
                        data-testid="course-question-feedback"
                    >
                        {FeedbackIcon && <FeedbackIcon className="CourseQuestionFrame__feedback-icon" />}
                        <span className="CourseQuestionFrame__feedback-text">{feedbackText}</span>
                    </div>
                ) : (
                    <button
                        className="CourseQuestionFrame__submit-button"
                        onClick={handleSubmit}
                        disabled={selectedAnswers.length === 0 || isSubmitting}
                        data-testid="course-question-submit-button"
                    >
                        {i18n.translate('weezbeeCourse.question.submitAnswer')}
                    </button>
                )}

       
            </div>
        </div>
    );
};
